import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Calculator,
  ArrowRight,
  CheckCircle,
  Home,
  Heart,
  Users,
  Award,
  Clock,
  Shield,
  Star,
  Handshake,
  Phone
} from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  const values = [
    {
      icon: Heart,
      title: 'Family Values',
      description: 'As a family-owned business, we treat every customer like family, ensuring personal attention and care in every interaction.',
      color: 'text-red-600'
    },
    {
      icon: Handshake,
      title: 'Customer First',
      description: 'Our customers are at the heart of everything we do. We listen, understand, and deliver exactly what you need.',
      color: 'text-[#8CB89F]'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We go above and beyond expectations, delivering premium quality shutters with exceptional craftsmanship.',
      color: 'text-yellow-600'
    },
    {
      icon: Shield,
      title: 'Trust & Reliability',
      description: 'Independent and honest, we build lasting relationships based on trust, transparency, and reliable service.',
      color: 'text-green-600'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 to-blue-50 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              About Aston Lily
              <span className="block text-slate-700">Our Family Story</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Named after our beloved children Aston and Lily, we're a proud family-owned business 
              dedicated to transforming homes with premium plantation shutters and exceptional personal service.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="lg:flex items-center gap-12">
            <div className="lg:w-1/2 mb-8 lg:mb-0">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/79f2e9a3e46d63c12d78f97eafcab5112c7aa1e3bfaeb76a3fc34cf30ef86b44.png"
                alt="Family business"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
            </div>
            <div className="lg:w-1/2">
              <div className="flex items-center gap-2 mb-4">
                <Heart className="h-6 w-6" style={{color: '#8CB89F'}} />
                <Badge variant="outline" className="border-green-300" style={{color: '#8CB89F'}}>Named After Our Children</Badge>
              </div>
              
              <h2 className="text-4xl font-bold text-gray-900 mb-6">The Aston Lily Story</h2>
              <div className="space-y-4 text-lg text-gray-600 leading-relaxed">
                <p>
                  Aston Lily Shutters was born from love - named after our precious children Aston and Lily with the hope that one day, they will want to join the business and carry on what we have started. What began as a dream to create something meaningful for our family has grown into a trusted name in premium plantation shutters.
                </p>
                <p>
                  As a family business, we understand the importance of home. Every shutter we install is made with the same care and attention we'd want for our own children's rooms. We remain proudly independent, allowing us to focus on what matters most - our customers and the quality of our work.
                </p>
                <p>
                  Being family-owned means we can make decisions quickly, adapt to your needs, and maintain the personal touch that larger corporations simply can't match. Every customer matters to us, and we treat your home with the same love we show our own family's.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <value.icon className={`h-12 w-12 ${value.color} mx-auto mb-4`} />
                  <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Us?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The advantages of working with an independent, family-owned business
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                  <Users className="h-6 w-6 text-[#8CB89F]" />
                  Personal Service
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Direct access to owners and decision makers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Personalized solutions for your specific needs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Flexible approach to meet your requirements</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Ongoing relationship and support</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                  <Star className="h-6 w-6 text-yellow-600" />
                  Going Above & Beyond
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Extra time and attention to get everything perfect</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Post-installation support and maintenance advice</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Problem-solving approach to any challenges</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Genuine care for your satisfaction and happiness</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Experience the Family Difference</h2>
          <p className="text-xl mb-8 text-gray-300">
            Join thousands of satisfied customers who've experienced our commitment to excellence and personal service.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button size="lg" className="bg-[#8CB89F] hover:bg-[#7aa88d] px-8 py-4 text-lg">
                <Calculator className="mr-2 h-5 w-5" />
                Use Our Online Estimator
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 text-lg"
              >
                <Phone className="mr-2 h-5 w-5" />
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;